import React, { Component } from "react";
import { connect } from "react-redux";
import Popup from "reactjs-popup";
import Alert from "react-s-alert";
import {
  getShoppingCartOrders,
  setOrderLoading,
  syncStripeOrders,
  refreshStripeOrders,
  getSyncState,
  getStripeSyncSummary,
  getSchedulerStatus
} from "../../actions/oneShoppingCartActions";
import PropTypes from "prop-types";
import Link from "../../../node_modules/react-router-dom/Link";
import Menu from "../common/Menu";
import OrderList from "./OrderList";
import StripeSyncDetailsPanel from "./StripeSyncDetailsPanel";

class Orders extends Component {
  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      syncSummary: null,
      hasCompletedFullSync: null,
      schedulerStatus: null,
      isDetailsOpen: false,
      isDetailsLoading: false,
      detailsSummary: null,
      detailsHistory: [],
      dateSortOrder: "desc",
      latestSyncOrderIds: []
    };

    this.dismissSyncSummary = this.dismissSyncSummary.bind(this);
    this.handleDateSortChange = this.handleDateSortChange.bind(this);
    this.openDetailsPanel = this.openDetailsPanel.bind(this);
    this.closeDetailsPanel = this.closeDetailsPanel.bind(this);
    this.handleAlertClick = this.handleAlertClick.bind(this);
    this.handleHeaderDetailsClick = this.handleHeaderDetailsClick.bind(this);
  }

  componentDidMount() {
    const { page } = this.state;

    this.props.getSyncState().then(syncState => {
      this.setState({
        hasCompletedFullSync: syncState ? syncState.hasCompletedFullSync : true
      });
    });

    this.props.getStripeSyncSummary().then(summary => {
      this.setState({
        detailsSummary: summary || null
      });
    });

    this.props.getSchedulerStatus().then(schedulerStatus => {
      this.setState({
        schedulerStatus: schedulerStatus || null
      });
    });

    document.addEventListener("click", this.handleAlertClick);

    if (this.props.match.params.pageNum) {
      this.props.setOrderLoading();
      this.props.getShoppingCartOrders(this.props.match.params.pageNum);
      this.setState({
        page: this.props.match.params.pageNum
      });
    } else {
      this.props.setOrderLoading();
      this.props.getShoppingCartOrders(page);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleAlertClick);
  }

  loadPage(page) {
    this.props.setOrderLoading();
    this.props.getShoppingCartOrders(page);
    this.setState({ page });
  }

  syncOrders() {
    this.props.syncStripeOrders(this.state.page)
      .then(summary => {
        const nextSummary = summary || null;
        const nextDetailsSummary = this.buildDetailsSummary(nextSummary);
        const nextLatestSyncOrderIds = Array.isArray(nextSummary && nextSummary.latestSyncOrderIds)
          ? nextSummary.latestSyncOrderIds
          : [];

        this.setState({
          syncSummary: nextSummary,
          detailsSummary: nextDetailsSummary,
          latestSyncOrderIds: nextLatestSyncOrderIds,
          hasCompletedFullSync:
            nextSummary && typeof nextSummary.isFullSync === "boolean"
              ? true
              : this.state.hasCompletedFullSync
        });

        this.props.getSchedulerStatus().then(schedulerStatus => {
          this.setState({ schedulerStatus: schedulerStatus || null });
        });

        Alert.success(
          `Stripe sync completed. ${nextLatestSyncOrderIds.length} new orders imported.`,
          {
            effect: "scale",
            onClose: () => {},
          },
        );
      })
      .catch(() => {
        this.setState({ syncSummary: null });
      });
  }

  refreshOrders() {
    this.props.refreshStripeOrders(this.state.page)
      .then(summary => {
        this.setState({
          syncSummary: summary || null,
          hasCompletedFullSync: this.state.hasCompletedFullSync
        });
      })
      .catch(() => {
        this.setState({ syncSummary: null });
      });
  }

  dismissSyncSummary() {
    this.setState({ syncSummary: null });
  }

  openDetailsPanel() {
    const activeSummary = this.state.detailsSummary || this.state.syncSummary || null;

    this.setState({
      isDetailsOpen: true,
      isDetailsLoading: true,
      detailsSummary: activeSummary,
      detailsHistory: this.buildDetailsHistory(activeSummary),
    });

    Promise.all([
      this.props.getStripeSyncSummary(),
      this.props.getSchedulerStatus(),
    ])
      .then(([summary, schedulerStatus]) => {
        const nextSummary = summary || activeSummary;
        const nextSchedulerStatus = schedulerStatus || this.state.schedulerStatus;

        this.setState({
          detailsSummary: nextSummary,
          schedulerStatus: nextSchedulerStatus,
          detailsHistory: this.buildDetailsHistory(nextSummary, nextSchedulerStatus),
          isDetailsLoading: false,
        });
      })
      .catch(() => {
        this.setState({ isDetailsLoading: false });
      });
  }

  closeDetailsPanel() {
    this.setState({ isDetailsOpen: false });
  }

  handleHeaderDetailsClick(event) {
    if (event) {
      event.preventDefault();
    }
    this.openDetailsPanel();
  }

  handleAlertClick(event) {
    const link = event.target && event.target.closest ? event.target.closest("a[data-action='open-sync-drawer']") : null;
    if (link) {
      this.handleHeaderDetailsClick(event);
    }
  }

  buildDetailsSummary(summary) {
    const timestamp = new Date().toISOString();
    const imported = summary && summary.imported !== undefined ? summary.imported : 0;
    const alreadyExisting = summary && summary.alreadyExisting !== undefined ? summary.alreadyExisting : 0;
    const failed = summary && summary.failed !== undefined ? summary.failed : 0;

    return {
      ...(summary || {}),
      lastSuccessfulSync: timestamp,
      updatedAt: timestamp,
      reconciliation: {
        totalImported: imported,
        totalSkipped: alreadyExisting,
        totalFailed: failed,
      },
    };
  }

  buildDetailsHistory(activeSummary = null, activeSchedulerStatus = null) {
    const history = [];
    const summary = activeSummary || this.state.detailsSummary || this.state.syncSummary || null;
    const schedulerStatus = activeSchedulerStatus || this.state.schedulerStatus || null;

    if (summary) {
      history.push({
        timestamp: summary.lastSuccessfulSync || summary.updatedAt || new Date().toISOString(),
        trigger: "Manual",
        imported: summary.reconciliation && summary.reconciliation.totalImported !== undefined ? summary.reconciliation.totalImported : summary.imported || 0,
        alreadyExisting: summary.reconciliation && summary.reconciliation.totalSkipped !== undefined ? summary.reconciliation.totalSkipped : summary.alreadyExisting || 0,
        failed: summary.reconciliation && summary.reconciliation.totalFailed !== undefined ? summary.reconciliation.totalFailed : summary.failed || 0,
      });
    } else if (schedulerStatus && schedulerStatus.lastSuccessfulSync) {
      history.push({
        timestamp: schedulerStatus.lastSuccessfulSync,
        trigger: schedulerStatus.status === "running" ? "Scheduler" : "Manual",
        imported: schedulerStatus.totalImported || 0,
      });
    }

    return history.slice(0, 10);
  }

  handleDateSortChange(event) {
    this.setState({
      dateSortOrder: event.target.value
    });
  }

  sortOrders(orders) {
    const sortedOrders = [...orders];

    sortedOrders.sort((a, b) => {
      const dateA = this.getOrderTime(a);
      const dateB = this.getOrderTime(b);

      if (dateA === dateB) {
        return 0;
      }

      return this.state.dateSortOrder === "asc"
        ? dateA - dateB
        : dateB - dateA;
    });

    return sortedOrders;
  }

  getOrderTime(order) {
    const value = order && (order.OrderedAt || order.orderedAt || order.createdAt || order.creationDate || order.completionDate);
    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
  }

  formatLastSyncLabel(timestamp) {
    if (!timestamp) {
      return "Not run yet";
    }

    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) {
      return "Not run yet";
    }

    return parsed.toLocaleString();
  }

  renderPages(numOfPages) {
    let pages = [];
    for (let i = 1; i <= numOfPages; i++) {
      pages.push(
        <div key={i} className="pageCounter" onClick={() => this.loadPage(i)}>
          <Link to={"/admin/dashboard/orders/" + i} className="mr-2" key={i}>
            {i}
          </Link>
        </div>
      );
    }
    return pages;
  }

  render() {
    let numOfPages = "loading..";
    let orders = [];

    const { auth } = this.props;
    const isAdmin = auth && auth.user && auth.user.role === "Admin";
    const isShow = isAdmin;

    if (this.props.orders.orders !== null) {
      numOfPages = this.props.orders.orders.pages;
      orders = this.props.orders.orders.orders;
    }

    const sortedOrders = this.sortOrders(orders);
    const lastSyncTimestamp = this.state.detailsSummary && this.state.detailsSummary.lastSuccessfulSync
      ? this.state.detailsSummary.lastSuccessfulSync
      : (this.state.schedulerStatus && this.state.schedulerStatus.lastSuccessfulSync ? this.state.schedulerStatus.lastSuccessfulSync : null);
    const lastSyncLabel = this.formatLastSyncLabel(lastSyncTimestamp);
    return (
      <div className="wrapper-alt">
        <Menu />
        {!isShow && <div className="p-5" /> }
        {isShow && <div className="container">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: "12px" }}>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
              <h1 className="mb-0">Orders</h1>
              <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => this.syncOrders()}
                  disabled={this.props.orders.loading}
                >
                  Run Sync Now
                </button>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => this.refreshOrders()}
                  disabled={this.props.orders.loading}
                >
                  Refresh Orders
                </button>
                <div className="d-flex align-items-center" style={{ minWidth: "140px" }}>
                  <label className="mr-2 mb-0" htmlFor="order-date-sort">Sort</label>
                  <select
                    id="order-date-sort"
                    className="form-control"
                    value={this.state.dateSortOrder}
                    onChange={this.handleDateSortChange}
                    disabled={this.props.orders.loading}
                    style={{ minWidth: "140px" }}
                  >
                    <option value="desc">Newest Date</option>
                    <option value="asc">Oldest Date</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center flex-wrap text-muted small" style={{ gap: "8px" }}>
              <span className="text-success">●</span>
              <span>Auto Sync</span>
              <span>•</span>
              <span>Last Sync {lastSyncLabel}</span>
              <span>•</span>
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={this.handleHeaderDetailsClick}
              >
                View Sync Details
              </button>
            </div>
          </div>
          <Popup
            position="right center"
            modal={true}
            open={this.state.isDetailsOpen}
            closeOnDocumentClick={false}
            onClose={this.closeDetailsPanel}
            contentStyle={{
              position: "fixed",
              zIndex: "1000",
              height: "100%",
              width: "40vw",
              padding: "30px",
              right: "0",
              top: "0",
              overflowY: "auto",
              background: "#fff",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
            }}
            overlayStyle={{ background: "rgba(0,0,0,0.35)" }}
            arrow={false}
            closeOnEscape={true}
          >
            <div className="modal-container">
              <StripeSyncDetailsPanel
                summary={this.state.detailsSummary}
                schedulerStatus={this.state.schedulerStatus}
                history={this.state.detailsHistory}
                isLoading={this.state.isDetailsLoading}
                onClose={this.closeDetailsPanel}
              />
            </div>
          </Popup>
          {this.state.syncSummary && (
            <div className={`alert ${this.state.syncSummary.failed > 0 ? "alert-warning" : "alert-success"} mt-2 d-flex justify-content-between align-items-start`}>
              <div>
                Sync completed for {this.state.syncSummary.checked || 0} orders. {this.state.latestSyncOrderIds.length} is new, {Math.max((this.state.syncSummary.checked || 0) - (this.state.syncSummary.imported || 0) - (this.state.syncSummary.failed || 0), 0)} are already synced, and {this.state.syncSummary.failed || 0} order failed to sync.
                {this.state.syncSummary.errors && this.state.syncSummary.errors.length > 0 && (
                  <div className="mt-2">
                    Reason: {this.state.syncSummary.errors[0].message || "Unknown error"}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="close ml-3"
                aria-label="Close"
                onClick={this.dismissSyncSummary}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          )}
          {this.props.orders.loading === false ? (
            this.props.errors && this.props.errors.message ? (
              <div className="alert alert-danger">{this.props.errors.message}</div>
            ) : (
              <OrderList
                orders={sortedOrders}
                latestSyncOrderIds={this.state.latestSyncOrderIds}
                page={this.state.page}
              />
            )
          ) : (
            <div className="text-center">
              <img src={require("./loading.gif")} alt="" />
            </div>
          )}

          <div className="d-flex flex-row flex-wrap">
            {this.renderPages(numOfPages)}
          </div>
        </div>}
      </div>
    );
  }
}

Orders.propTypes = {
  auth: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  auth: state.auth,
  errors: state.errors,
  members: state.members,
  orders: state.orders
});

export default connect(
  mapStateToProps,
  { getShoppingCartOrders, setOrderLoading, syncStripeOrders, refreshStripeOrders, getSyncState, getStripeSyncSummary, getSchedulerStatus }
)(Orders);
